import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '../lib/cn';

/**
 * Inputs, from section 05 of the kit.
 *
 * A field at rest has a 1px line-strong border; focused it goes to 1.5px ink;
 * in error it goes to 1px alert with the reason beside it, never a bare red
 * outline the user has to guess at.
 */

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  icon?: ReactNode;
  /** Right-aligned hint, e.g. "Over budget". Turns the field alert-coloured. */
  error?: string;
  /** Monospace, for durations and timer values. */
  mono?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { icon, error, mono, className, ...props },
  ref,
) {
  return (
    <div
      className={cn(
        'flex h-10 items-center gap-[9px] rounded-pill px-[14px] transition-colors duration-base ease-emphasis',
        'border bg-paper focus-within:border-[1.5px] focus-within:border-ink',
        error ? 'border-alert' : 'border-line-strong',
        className,
      )}
    >
      {icon && <span className="shrink-0 text-muted">{icon}</span>}
      <input
        ref={ref}
        aria-invalid={!!error}
        className={cn(
          'min-w-0 flex-1 bg-transparent text-body text-ink outline-none placeholder:text-faint',
          mono && 'font-mono',
        )}
        {...props}
      />
      {error && <span className="shrink-0 text-meta-sm text-alert">{error}</span>}
    </div>
  );
});

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: string }
>(function TextArea({ error, className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={!!error}
      className={cn(
        'w-full resize-none rounded-panel border bg-paper px-[14px] py-3 text-body text-ink outline-none',
        'transition-colors duration-base ease-emphasis placeholder:text-faint focus:border-[1.5px] focus:border-ink',
        error ? 'border-alert' : 'border-line-strong',
        className,
      )}
      {...props}
    />
  );
});

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-meta font-medium text-muted">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-meta-sm text-alert">{error}</p>
      ) : (
        hint && <p className="text-meta-sm text-muted">{hint}</p>
      )}
    </div>
  );
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  labelHidden,
  id,
  disabled,
}: {
  /** `'indeterminate'` renders the mixed dash, for a partly-selected tree. */
  checked: boolean | 'indeterminate';
  onCheckedChange: (checked: boolean) => void;
  label?: string;
  /**
   * Keep the label as the accessible name without drawing it. A dense row has
   * no space for "Select August content calendar" beside the box, and a
   * visually hidden label still takes layout width.
   */
  labelHidden?: boolean;
  id?: string;
  disabled?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      <CheckboxPrimitive.Root
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onCheckedChange(next === true)}
        aria-label={label}
        className={cn(
          'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px]',
          'transition-colors duration-base ease-emphasis disabled:opacity-40',
          checked === false ? 'border-line-strong bg-transparent' : 'border-ink bg-ink',
        )}
      >
        <CheckboxPrimitive.Indicator>
          {checked === 'indeterminate' ? (
            <span className="block h-0.5 w-[9px] rounded-[1px] bg-on-ink" />
          ) : (
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden>
              <path
                d="M2.6 5.6L4.4 7.4L8.4 3.4"
                stroke="var(--on-ink)"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label && !labelHidden && (
        <label htmlFor={id} className={cn('text-chip', checked ? 'text-ink' : 'text-muted')}>
          {label}
        </label>
      )}
    </span>
  );
}

/** Radio, used for the billable / internal choice. */
export function RadioOption({
  selected,
  onSelect,
  label,
  name,
}: {
  selected: boolean;
  onSelect: () => void;
  label: string;
  name: string;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2">
      <input
        type="radio"
        name={name}
        checked={selected}
        onChange={onSelect}
        className="sr-only peer"
      />
      <span
        className={cn(
          'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px]',
          'transition-colors duration-base ease-emphasis peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ink',
          selected ? 'border-ink' : 'border-line-strong',
        )}
      >
        {selected && <span className="h-[9px] w-[9px] rounded-pill bg-ink" />}
      </span>
      <span className={cn('text-chip', selected ? 'text-ink' : 'text-muted')}>{label}</span>
    </label>
  );
}

export function Toggle({
  checked,
  onCheckedChange,
  label,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <SwitchPrimitive.Root
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      aria-label={label}
      className={cn(
        'inline-flex h-[22px] w-10 shrink-0 items-center rounded-pill px-[3px]',
        'transition-colors duration-base ease-emphasis disabled:opacity-40',
        checked ? 'bg-ink' : 'bg-line',
      )}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'block h-4 w-4 rounded-full bg-paper transition-transform duration-base ease-emphasis',
          checked ? 'translate-x-[18px]' : 'translate-x-0',
        )}
      />
    </SwitchPrimitive.Root>
  );
}
